/**
 * TodayView — "What should I do today?"
 *
 * Replaces the metrics-heavy Dashboard with an action-oriented view:
 * 1. Greeting + top 3 priority accounts (from Queue)
 * 2. Deal briefings (if any)
 * 3. Recent alerts stream
 * 4. Setup nudge (if onboarding incomplete)
 *
 * PhD panel recommendation: "The first thing the user sees should be
 * 3 accounts with thermometers and one-line reasons."
 */

import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowRight, Bell, FileText, Zap } from 'lucide-react'
import { Card } from '@/components/UI/Card/Card'
import { Button } from '@/components/UI/Button/Button'
import { HeatThermometer } from '@/components/TAG/HeatThermometer'
import { OnboardingChecklist } from '@/components/UI/OnboardingChecklist'
import { useSetupStatus } from '@/hooks/useSetupStatus'
import { useNudgeContext } from '@/hooks/useNudgeContext'
import { http } from '@/services/api'
import { CsvImportModal } from '@/components/Import/CsvImportModal'
import SmartQueue from '@/components/SmartQueue/SmartQueue'
import { CommandCard, type CommandCardProps } from '@/components/Dashboard/CommandCard'
import { useSmartQueueStore } from '@/stores/smartQueueStore'
import { clientConfig } from '@/config'
import { dealBriefingApi } from '@/services/dealBriefingApi'

interface PriorityAccount {
  id: string
  prospect_id: string
  prospect_name: string
  prospect_company?: string
  action_type: string
  why_this_action?: string[]
  tag_score?: number
  tag_trend?: string
}

interface Briefing {
  id: string
  company_name: string
  tag_score: number
  trend: string
  priority: string
  status: string
  created_at: string
}

interface Alert {
  id: string
  title: string
  severity: string
  created_at: string
  acknowledged: boolean
}

const ACTION_LABELS: Record<string, string> = {
  hot_lead: 'Hot lead — act now',
  follow_up_due: 'Follow-up overdue',
  meeting_request: 'Meeting requested',
  stale_prospect: 'Re-engage',
  qualified_lead: 'Qualified — reach out',
}

const getGreeting = (): string => {
  const hour = new Date().getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

export const TodayView: React.FC = () => {
  const navigate = useNavigate()
  const nudgeCtx = useNudgeContext()
  const setupStatus = useSetupStatus(nudgeCtx)
  // Cold-start ONLY once setup status has loaded — `hasTargetCompanies` initializes to
  // false while `/tag/stats` is in flight, so keying on it directly would flash the
  // "no accounts yet" activation copy to an established tenant whose queue is momentarily
  // empty (telling an active customer they have nothing). Gate on !loading.
  const isColdStart = !setupStatus.loading && !setupStatus.hasTargetCompanies
  const awaitingFirstOutcome = setupStatus.loading || setupStatus.steps.some(
    step => step.id === 'first_operation' && !step.completed
  )
  const [priorities, setPriorities] = useState<PriorityAccount[]>([])
  const [briefings, setBriefings] = useState<Briefing[]>([])
  const [alerts, setAlerts] = useState<Alert[]>([])
  const [loading, setLoading] = useState(true)
  const snoozeAction = useSmartQueueStore(state => state.snoozeAction)
  const smartQueueRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true)
      try {
        const [queueRes, briefingRes, alertRes] = await Promise.allSettled([
          http<PriorityAccount[]>('/queue', { method: 'GET', query: { limit: 3 } }),
          clientConfig.features.enableDealBriefing
            ? dealBriefingApi.list(1, 3)
            : Promise.resolve({ items: [] }),
          http<{ alerts: Alert[] }>('/alerts', { method: 'GET', query: { limit: 3, status: 'new' } }),
        ])

        if (queueRes.status === 'fulfilled') setPriorities(Array.isArray(queueRes.value) ? queueRes.value.slice(0, 3) : [])
        if (briefingRes.status === 'fulfilled') {
          setBriefings(
            briefingRes.value.items.slice(0, 3).map(briefing => ({
              id: briefing.id,
              company_name: briefing.company_name,
              tag_score: briefing.tag_score ?? 50,
              trend: briefing.trend_label ?? 'stable',
              priority: briefing.priority.toUpperCase(),
              status: briefing.status,
              created_at: briefing.created_at,
            }))
          )
        }
        if (alertRes.status === 'fulfilled') setAlerts(alertRes.value.alerts || [])
      } catch {
        // Individual failures handled by Promise.allSettled
      } finally {
        setLoading(false)
      }
    }
    fetchAll()
  }, [])

  const heroAccount = useMemo(() => {
    const top = priorities[0]
    if (!top) return null
    const company = top.prospect_company || top.prospect_name
    const matchingBriefing = top.prospect_company
      ? briefings.find(b => b.company_name?.toLowerCase() === top.prospect_company?.toLowerCase())
      : undefined
    const trend = (top.tag_trend ?? 'stable') as CommandCardProps['trend']
    const priority = (matchingBriefing?.priority as CommandCardProps['priority']) || undefined

    const props: CommandCardProps = {
      companyName: company,
      tagScore: top.tag_score ?? 50,
      trend,
      priority,
      whyNow: top.why_this_action ?? [],
      onStartOutreach: () =>
        smartQueueRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
      onOpenBriefing: matchingBriefing
        ? () => navigate(`/app/deal-briefings/${matchingBriefing.id}`)
        : undefined,
      onSnooze: async () => {
        await snoozeAction(top.id, { hours: 2 })
        setPriorities(prev => prev.filter(p => p.id !== top.id))
      },
    }
    return props
  }, [priorities, briefings, navigate, snoozeAction])

  if (loading) {
    return (
      <div className="flex justify-center p-16">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--accent)]" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <CsvImportModal />

      {/* Greeting */}
      <div>
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          {getGreeting()}. <span style={{ color: 'var(--accent)' }}>Here's your day.</span>
        </h1>
        <p className="text-base mt-1" style={{ color: 'var(--text-muted)' }}>
          {priorities.length > 0
            ? `${priorities.length} accounts need your attention`
            : isColdStart
              ? 'Nothing needs your attention yet. Start with the outcome you want Ricald to move.'
              : 'Your queue is clear — nice work'}
        </p>
      </div>

      {/* Hero — #1 priority account as a CommandCard */}
      {heroAccount && <CommandCard {...heroAccount} />}

      {/* Onboarding checklist (only when steps remain) */}
      {!setupStatus.loading && setupStatus.completedCount < setupStatus.totalSteps && (
        <OnboardingChecklist
          steps={setupStatus.steps}
          nudgeKey="today-onboarding"
        />
      )}

      {/* Priority Accounts — runners-up to the hero CommandCard */}
      {priorities.length > 1 && (
        <div>
          <h2 className="text-lg font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
            Next up
          </h2>
          <div className="space-y-3">
            {priorities.slice(1).map(item => (
              <div
                key={item.id}
                className="p-4 rounded-lg"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <HeatThermometer score={item.tag_score || 50} trend={item.tag_trend || 'stable'} size="sm" />
                    <div>
                      <div className="text-base font-medium" style={{ color: 'var(--text-primary)' }}>{item.prospect_name}</div>
                      <div className="text-base" style={{ color: 'var(--text-muted)' }}>
                        {item.prospect_company || ''} · {ACTION_LABELS[item.action_type] || item.action_type.replace(/_/g, ' ')}
                      </div>
                    </div>
                  </div>
                  {item.why_this_action && item.why_this_action.length > 0 && (
                    <span className="text-base hidden sm:block max-w-xs truncate" style={{ color: 'var(--text-muted)' }}>
                      {item.why_this_action[0]}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Do not compete with task-first activation. The queue becomes useful
          after the customer has delegated an outcome; before that, its legacy
          "add accounts" empty state presents a second onboarding path. */}
      {!awaitingFirstOutcome && (
        <div ref={smartQueueRef}>
          <SmartQueue coldStart={isColdStart} />
        </div>
      )}

      {/* Deal Briefings */}
      {briefings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <FileText className="w-5 h-5" style={{ color: 'var(--accent)' }} />
              Deal Briefings
            </h2>
            <Button onClick={() => navigate('/app/deal-briefings')} variant="outline" size="sm">
              View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {briefings.map(b => (
              <Card key={b.id}>
                <div className="flex items-center gap-2 mb-2">
                  <HeatThermometer score={b.tag_score} trend={b.trend} size="sm" />
                  <span className="text-base font-medium" style={{ color: 'var(--text-primary)' }}>
                    {b.company_name}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className="text-xs px-1.5 py-0.5 rounded font-medium"
                    style={{
                      background: b.priority === 'P0' ? 'color-mix(in srgb, var(--state-danger) 15%, transparent)' : 'color-mix(in srgb, var(--state-warning) 15%, transparent)',
                      color: b.priority === 'P0' ? 'var(--state-danger)' : 'var(--state-warning)',
                    }}
                  >
                    {b.priority}
                  </span>
                  <span className="text-base" style={{ color: 'var(--text-muted)' }}>
                    {new Date(b.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recent Alerts */}
      {alerts.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
              <Bell className="w-5 h-5" style={{ color: 'var(--state-warning)' }} />
              New Alerts
            </h2>
            <Button onClick={() => navigate('/app/alerts')} variant="outline" size="sm">
              View All <ArrowRight className="w-3.5 h-3.5 ml-1" />
            </Button>
          </div>
          <div className="space-y-2">
            {alerts.map(alert => (
              <div
                key={alert.id}
                className="flex items-center gap-3 p-3 rounded-lg text-base"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
              >
                <Zap className="w-4 h-4 flex-shrink-0" style={{ color: 'var(--state-warning)' }} />
                <span className="flex-1" style={{ color: 'var(--text-primary)' }}>{alert.title}</span>
                <span className="text-base" style={{ color: 'var(--text-muted)' }}>
                  {new Date(alert.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No separate empty state — SmartQueue handles its own empty state */}
    </div>
  )
}
